import Vue from 'vue'
import Vuex from 'vuex'

import admin from './modules/admin'
import calc from './modules/calc'
import user from './modules/user'
import {apiAx} from './common'
import {uid} from '../utils'

Vue.use(Vuex)

export default new Vuex.Store({
  modules: {
    calc: calc,
    admin: admin,
    user: user
  },
  state: {
    loader: 0,
    loaderLabels: {}
  },
  mutations: {
    INC_LOADER: (state, params) => {
      state.loader++
      if (params && params.label && params.uuid) {
        Vue.set(state, 'loaderLabels', Object.assign(state.loaderLabels, {[params.uuid]: params.label}))
      }
    },
    DEC_LOADER: (state, uuid) => {
      if (uuid) {
        Vue.delete(state.loaderLabels, uuid)
      }
      if (state.loader > 0) {
        state.loader--
      }
    }
  },
  actions: {
    apiFetchClientNames: apiCall(
      () => apiAx.get('/clients'),
      'Fetching clients...'
    ),
    apiFetchAgentNames: apiCall(
      () => apiAx.get('/agents'),
      'Fetching agents...'
    ),
    apiFetchMeasureUnitNames: apiCall(
      () => apiAx.get('/measure-units'),
      'Fetching measure units...'
    ),
    apiFetchMeasureUnitsInfo: apiCall(
      () => apiAx.get('/measure-units-info'),
      'Fetching measure units info...'
    ),
    apiFetchSurchargeTypeNames: apiCall(
      () => apiAx.get('/surcharge-types'),
      'Fetching surcharge types...'
    ),
    apiFetchCurrencyNames: apiCall(
      () => apiAx.get('/currencies'),
      'Fetching currencies...'
    ),
    apiFetchLocations: apiCall(
      searchString => apiAx.get(`/locations?searchString=${searchString || ''}`),
      'Fetching locations...'
    ),
    apiFetchAirlines: apiCall(
      () => apiAx.get('/airlines'),
      'Fetching airlines...'
    ),

    apiGetLabels: apiCall(() => apiAx.get('/labels')),
    apiGetRelationshipTypes: apiCall(() => apiAx.get('/rels')),
    apiGetNodes: apiCall(label => apiAx.get(`/nodes/${label}`), `Fetching nodes..."`),
    apiGetProps: apiCall(label => apiAx.get(`/props/${label}`)),
    apiGetPropValues: apiCall(({label, prop}) => apiAx.get(`/props/${label}/${prop}`)),
    apiNodeCreate: apiCall(label => apiAx.put(`/nodes/${label}`)),
    apiNodeGetRelationshipTypes: apiCall(uuid => apiAx.get(`/node/${uuid}/rels`), 'Fetching node relationship types...'),
    apiNodeGetRelatedNodes: apiCall(({uuid, relType}) => {
      return apiAx.get(`/node/${uuid}/rels/${relType.type}?direction=${relType.direction}`)
    }),

    apiNodeGetProperties: apiCall(uuid => apiAx.get(`/node/${uuid}`), 'Fetching node properties...'),
    apiNodeClone: apiCall(uuid => apiAx.put(`/node/${uuid}`), 'Cloning node...'),
    apiNodeDelete: apiCall(uuid => apiAx.delete(`/node/${uuid}`), 'Deleting node...'),
    apiNodeSetProperties: apiCall(({uuid, props}) => apiAx.post(`/node/${uuid}`, props)),

    apiRelationshipGetProperties: apiCall(uuid => apiAx.get(`/rel/${uuid}`), 'Fetching relationship properties...'),
    apiRelationshipClone: apiCall(uuid => apiAx.put(`/rel/${uuid}`)),
    apiRelationshipDelete: apiCall(uuid => apiAx.delete(`/rel/${uuid}`)),
    apiRelationshipSetProperties: apiCall(({uuid, props}) => apiAx.post(`/rel/${uuid}`, props)),

    apiUpdateNodesProperties: apiCall(data => apiAx.post('/update/properties', data)),

    apiRelationshipSetNodes: apiCall(({uuid, from, to}) => {
      return apiAx.put(`/rel/${uuid}/nodes/${from}/${to}`)
    }),
    apiRelationshipCreate: apiCall(({type, from, to}) => {
      return apiAx.put(`/rels/${type}/nodes/${from}/${to}`)
    }),

    apiCartValidateRootRates: apiCall(uuids => {
      return apiAx.post('/validate/root-rates', uuids)
    }),
    apiCartGetRatesChildrenTypes: apiCall(uuids => {
      return apiAx.post('/rates/children', uuids)
    }),
    apiGenerateRates: apiCall(data => apiAx.post('/generate/rates', data)),

    apiComplaintCreate: apiCall(data => apiAx.put('/complaints', data)),
    apiComplaintDelete: apiCall(uuid => apiAx.delete(`/complaints/${uuid}`)),
    apiComplaintsGet: apiCall(() => apiAx.get('/complaints')),

    apiSearchTemplatesGet: apiCall(() => apiAx.get('/search-templates')),
    apiSearchTemplateCreate: apiCall(data => apiAx.put('/search-templates', data)),
    apiSearchTemplate: apiCall(({uuid, vars}) => apiAx.post(`/search-templates/${uuid}`, vars)),
    apiSearchTemplateDelete: apiCall(uuid => apiAx.delete(`/search-templates/${uuid}`)),

    apiOrdersCreate: apiCall(data => apiAx.put('/orders', data)),
    apiOrdersGet: apiCall(() => apiAx.get('/orders')),
    apiOrdersGetSimple: apiCall(data => apiAx.post('/orders-simple', data)),
    apiOrderGetInfo: apiCall((uuid) => apiAx.get(`/order/${uuid}`)),

    apiOrderSetName: apiCall(({uuid, name}) => apiAx.post(`/order/${uuid}`, {name: name})),
    apiOrderDelete: apiCall(uuid => apiAx.delete(`/order/${uuid}`)),
    apiOrderQuoteSetRatePrice: apiCall(({quoteUuid, rateUuid, price}) => {
      return apiAx.post(`/order/quote/${quoteUuid}/rate/${rateUuid}/price`, {price: price})
    }),

    apiOrderMoveRequest: apiCall(({type, orderUuid, requestUuid}) => {
      return apiAx.put(`/order/${orderUuid}/request/${requestUuid}/move`, {type})
    }),

    apiOrdersMerge: apiCall(data => apiAx.post('/orders', data)),
    apiCalculateRates: apiCall(
      data => apiAx.post('/calculate', data),
      'Searching direct rates...'
    ),

    apiCalculateConnectedRates: apiCall(
      data => apiAx.post('/calculate-connected', data),
      'Searching connected rates...'
    ),
    apiCalculateCargoParameters: apiCall(
      data => apiAx.post('/cargo-parameters', data),
      'Calculating available cargo parameters...'
    ),
    apiCalculateDirectFlights: apiCall(
      data => apiAx.post('/calculate-direct-flights', data),
      'Searching for direct flights schedule...'
    ),
    apiCalculateConnectedFlights: apiCall(
      data => apiAx.post('/calculate-connected-flights', data),
      'Searching for connected flights schedule...'
    ),

    apiGetEventStatusesInfo: apiCall(() => apiAx.get('/calendar/event-statuses'), 'Fetching event statuses'),

    apiEventSetStatus: apiCall(({uuid, status}) => apiAx.post(`/calendar/event/${uuid}/status/${status}`)),
    apiGetEvents: apiCall(
      params => apiAx.get('/calendar/events', {params: params}),
      'Loading events...'
    ),
    apiGetEvent: apiCall(uuid => apiAx.get(`/calendar/event/${uuid}`), 'Loading event information...'),
    apiGetEventTypes: apiCall(
      all => apiAx.post('/calendar/event-types', {all}),
      'Fetching event types...'
    ),
    apiGetEventAction: apiCall(
      code => apiAx.get('/calendar/event-action', {params: {code: code}}),
      'Loading Event Action...'
    ),
    apiGetEventStatusChangeRules: apiCall(
      () => apiAx.get('/calendar/event-status-change-rules'),
      'Fetching Event Status Change Rules...'
    ),
    apiChangeEventStatusChangeRule: apiCall(
      rule => apiAx.post('/calendar/event-status-change-rules', rule),
      'Fetching Event Status Change Rules...'
    ),
    apiGetEntitiesCodeName: apiCall(
      entity => apiAx.get('/calendar/entities-code-name', {params: {entity: entity}}),
      'Fetching entities...'
    ),
    apiEventCreate: apiCall(data => apiAx.put('/calendar/event', data), 'Creating event...'),
    apiEventUpdate: apiCall(data => apiAx.post('/calendar/event', data), 'Updating event...'),

    apiGetProfile: apiCall(() => apiAx.get('/profile')),
    apiProfileSetRatesTableColumns: apiCall(columns => apiAx.post(`/profile/rates-columns`, columns)),
    apiProfileSetFlightsTableColumns: apiCall(columns => apiAx.post('/profile/flights-columns', columns)),
    apiProfileSetOrdersTableColumns: apiCall(({columns, type}) => apiAx.post(`/profile/orders-columns/${type}`, columns))
  }
})

function apiCall (call, label = null) {
  return function ({commit}, params) {
    const uuid = uid()
    commit('INC_LOADER', label ? {uuid, label} : null)
    return call(params)
      .then(response => response && response.data)
      .finally(() => { commit('DEC_LOADER', uuid) })
  }
}



// WEBPACK FOOTER //
// ./src/store/index.js